import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import * as Yup from "yup";

import {
  maxLtContractDemandLoad,
  minHtContractDemandLoad,
  regexForName,
} from "common/constant";

import { getConsumerNumberValidation } from "utils/getConsumerNumberValidation";
import { getDocumentsValidationSchema } from "utils/getDocumentsValidationSchema";

import { useApplicantTypeQuery } from "apis/ApplicantType";

const useValidationSchema = () => {
  const formData = useSelector((state) => state?.PremiseAmalgmation);
  const { data: { data: { items: applicantType = [] } = {} } = {} } =
    useApplicantTypeQuery();

  const validationSchema = useMemo(
    () => [
      Yup.object({
        utilityConsumerId: Yup.string().required("Customer number is required"),
        connectionLoad: Yup.object({
          loadDetails: Yup.string().required("Load details is required"),
          supplyVoltage: Yup.string().required("Supply voltage is required"),
          tention: Yup.string().required("Please select tention"),

          increaseOfLoad:
            formData.connectionLoad.tention === "LT"
              ? Yup.number()
                  .required("Increase of load is required")
                  .test("isValidIncreaseOfLoadLT", "", function () {
                    const [{ value: current = null } = {}] = this.from || [];
                    if (current.increaseOfLoad < 0) {
                      return this.createError({
                        message: "Contract load must be positive",
                      });
                    }
                    if (
                      current.increaseOfLoad >
                      maxLtContractDemandLoad -
                        formData.connectionLoad.powerRequirement
                    ) {
                      return this.createError({
                        message:
                          "Invalid value, Please change power requirement to HT",
                      });
                    }
                    return true;
                  })
              : Yup.number()
                  .required("Increase of load is required")
                  .test("isValidIncreaseOfLoadHT", "", function () {
                    const [{ value: current = null } = {}] = this.from || [];
                    if (current.increaseOfLoad < 0) {
                      return this.createError({
                        message: "Increase of load must be positive",
                      });
                    }
                    if (
                      current.increaseOfLoad <
                      minHtContractDemandLoad -
                        formData.connectionLoad.powerRequirement
                    ) {
                      return this.createError({
                        message:
                          "Enter a value that makes the total power requirement more than 150",
                      });
                    }
                    if (
                      current.increaseOfLoad >
                      100000 - formData.connectionLoad.powerRequirement
                    ) {
                      return this.createError({
                        message: `Maximum value allowed for contract demand is ${
                          99999 - formData.connectionLoad.powerRequirement
                        }`,
                      });
                    }
                    return true;
                  }),
        }),
        applicant: Yup.object({
          panNumber: Yup.string()
            .matches(
              /^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/,
              "Please enter a valid Pan number"
            )
            .required("PAN number is required"),
          isGSTRegistered: Yup.string().required("GST is required"),
          gstNumber: Yup.string()
            .test("isGstRequired", "", function (_, context) {
              const [{ value: { isGSTRegistered, gstNumber } } = {}] =
                context.from || [];
              if (isGSTRegistered && !gstNumber) {
                return context.createError({
                  message: "GST number is required",
                });
              }
              return true;
            })
            .matches(
              /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}[Zz][0-9A-Za-z]{1}$/,
              "Please enter a valid GST number"
            ),
        }),
      }),
      Yup.object({
        authorizedPerson: Yup.object({
          salutation: Yup.string().required("Salutation is required"),
          firstName: Yup.string()
            .matches(regexForName, "Please enter a valid first name")
            .required("First name is required"),
          middleName: Yup.string().matches(
            regexForName,
            "Please enter a valid middle name"
          ),
          lastName: Yup.string().matches(
            regexForName,
            "Please enter a valid last name"
          ),
          // gender: Yup.string().required("Gender is required"),
          // ageCategory: Yup.string().required("Age category is required"),
          email: Yup.string()
            .email("Please enter valid email")
            .required("Email is required"),
          mobileNumber: Yup.string()
            .required("Mobile number is required")
            .matches(/^\d{10}$/, "Please enter valid mobile number"),
          landLine: Yup.string().matches(
            /^\d{8,11}$/,
            "Please enter a valid landline number"
          ),
          imageUrl: Yup.string().test(
            "imageRequired",
            "Please upload image",
            function () {
              const [{ value: current = null } = {}] = this.from || [];
              return !!(current?.image || current?.imageUrl);
            }
          ),
        }),
      }),
      Yup.object({
        utilityAddress: Yup.object({
          premisesAreaId: Yup.string().required("Premise Area is required"),
          zoneName: Yup.string().required("Zone is required"),
          blockName: Yup.string().required("Block is required"),
          // floors: Yup.array().min(1, "Floor is required"),
          roadName: Yup.string().required("Road name is required"),
          buildingName: Yup.string().required("Building is required"),
          otherZone: Yup.string().when("zoneName", {
            is: (zoneName) => zoneName === "other",
            then: Yup.string()
              .matches(
                /^[\w]+[\w\s.,-]*[\w\s.,-]+/,
                "Please enter valid zone name"
              )
              .required("Zone is required"),
            otherwise: Yup.string(),
          }),
          otherBlock: Yup.string().when("blockName", {
            is: (blockName) => blockName === "other",
            then: Yup.string()
              .matches(
                /^[\w]+[\w\s.,-]*[\w\s.,-]+/,
                "Please enter valid block name"
              )
              .required("Block Name is required"),
            otherwise: Yup.string(),
          }),
          otherBuilding: Yup.string().when("buildingName", {
            is: (buildingName) => buildingName === "other",
            then: Yup.string()
              .matches(
                /^[\w]+[\w\s.,-]*[\w\s.,-]+/,
                "Please enter valid building name"
              )
              .required("Building Name is required"),
            otherwise: Yup.string(),
          }),
          otherRoad: Yup.string().when("roadName", {
            is: (roadName) => roadName === "other",
            then: Yup.string()
              .matches(
                /^[\w]+[\w\s.,-]*[\w\s.,-]+/,
                "Please enter valid road name"
              )
              .required("Road name is required"),
            otherwise: Yup.string(),
          }),
          unitName: Yup.string().required("Unit ∕ Flat No is required"),
          nearestLandmark: Yup.string(),
        }),
      }),

      Yup.object({
        documents: getDocumentsValidationSchema(),
      }),
      Yup.object({
        declaration: Yup.object({
          isConsumerDuesOutStanding: Yup.boolean(),
          consumerDuesOutstanding: Yup.string().when(
            "isConsumerDuesOutstanding",
            {
              is: true,
              then: (schema) => getConsumerNumberValidation(schema),
              otherwise: (schema) => schema,
            }
          ),
          isPremisesDuesOutstanding: Yup.boolean(),
          premisesDuesOutstanding: Yup.string().when(
            "isPremisesDuesOutstanding",
            {
              is: true,
              then: (schema) => getConsumerNumberValidation(schema),
              otherwise: (schema) => schema,
            }
          ),
          isFirmDuesOutstanding: Yup.boolean(),
          firmDuesOutstanding: Yup.string().when("isFirmDuesOutstanding", {
            is: true,
            then: (schema) => getConsumerNumberValidation(schema),
            otherwise: (schema) => schema,
          }),
        }),
      }),
    ],
    [formData.utilityAddress, formData.connectionLoad]
  );

  const getValidationSchema = useCallback(
    (index) => validationSchema[index],
    [validationSchema]
  );
  return getValidationSchema;
};

export default useValidationSchema;
